<template>
  <div>
    <div class="row">
      <div class="col-sm-9">
        <h4 class="text-left font-weight-bold">REVISION NUMBER SETTINGS</h4>
      </div>
      <div class="col-sm-3 text-right">
        <button class="e-btn e-btn-green e-text-white pr-3 mt-2 float-right" @click="openCreateModal">
          <font-awesome-icon icon="plus-square" style="width: 25px" />
          <small class="font-weight-bold">CREATE REVISION</small>
        </button>
      </div>
    </div>

    <div class="row mt-2 mb-2">
      <div class="col-12 d-flex justify-content-between align-items-center">
        <nav>
          <ul class="pagination text-smallest mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="currentPage = 1">First</button>
            </li>
            <li
              class="page-item"
              v-for="page in visiblePages"
              :key="page"
              :class="{ active: page === currentPage }"
            >
              <button class="page-link" @click="currentPage = page">{{ page }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="currentPage = totalPages">Last</button>
            </li>
          </ul>
        </nav>
        <div style="width: 250px;">
          <form autocomplete="off" @submit.prevent>
            <input
              v-model="searchQuery"
              @input="currentPage = 1"
              type="text"
              class="form-control form-control-sm"
              placeholder="Search Revision Number"
            />
          </form>
        </div>
      </div>
    </div>

    <div class="row mt-3">
      <table class="table table-sm table-bordered small w-100">
        <thead>
        <tr>
          <th scope="col" class="text-center" style="width: 40px;">#</th>
          <th scope="col" class="text-center">VERSION</th>
          <th scope="col" class="text-center">DATE CREATED</th>
          <th scope="col" class="text-center">CREATED BY</th>
          <th scope="col" class="text-center">ACTION</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="loading">
          <td colspan="2"><loading /></td>
        </tr>
        <tr v-else v-for="(rev, index) in paginatedRevisions" :key="rev.id">
          <td >{{ (currentPage - 1) * perPage + index + 1 }}</td>
          <td style="width: 150px;">{{ rev.revision_number }}</td>
          <td>{{ formatDate(rev.created_at) }}</td>
          <td>
            {{ rev.created_by_user ? `${rev.created_by_user.last_name}, ${rev.created_by_user.first_name}` : '—' }}
          </td>
          <td style="width: 100px;">
            <div class="btn-group dropleft">
              <button class="mr-2 btn btn-sm btn-secondary force-white-all" data-toggle="dropdown">
                <font-awesome-icon icon="cogs" />
              </button>
              <div class="dropdown-menu dropdown-menu-right">
                <button class="dropdown-item text-secondary font-weight-bolder" @click="openUpdateModal(rev)">
                  <font-awesome-icon icon="pen" /> Update
                </button>
                <button class="dropdown-item text-danger font-weight-bolder" @click="confirmDelete(rev.id)">
                  <font-awesome-icon icon="trash" class="force-danger-all" /> Delete
                </button>
              </div>
            </div>
          </td>
        </tr>
        <tr v-if="paginatedRevisions.length === 0">
          <td class="text-center font-weight-bold text-muted" colspan="5">
            NO DATA SAVED
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <BaseModal :is-visible="showCreateModal" :close-button="false">
      <template v-slot:title><h4 class="font-weight-bold">CREATE REVISION</h4></template>
      <template v-slot:body>
        <form @submit.prevent="submitCreate">
          <div class="form-group">
            <label>
              Revision Number
              <span v-if="isCreateFormInvalid" class="text-danger font-weight-bold h6">*</span>
            </label>
            <input
              @input="clearRevisionError"
              placeholder="e.g., 1.0 / 11.11 / 1.1.1"
              type="text"
              v-model="form.revision_number"
              class="form-control"
              :class="{ 'is-invalid': isCreateFormInvalid || revisionErrors.revision_number }"
            />
            <small v-if="revisionErrors.revision_number" class="text-danger font-weight-bold">
              {{ revisionErrors.revision_number[0] }}
            </small>
            <small v-else-if="!form.revision_number.trim()" class="text-danger font-weight-bold">
              Revision number is required.
            </small>
            <small v-else-if="isFormatInvalid" class="text-danger font-weight-bold">
              Revision Number must be in format (e.g., 1.0 / 11.11 / 1.1.1)
            </small>
          </div>
        </form>
      </template>
      <template v-slot:footer>
        <button class="btn btn-sm btn-secondary" @click="onClickCancelCreate">Cancel</button>
        <button
          class="btn btn-sm btn-primary"
          :disabled="isCreateFormInvalid"
          @click="submitCreate"
        >
          Create
        </button>

      </template>
    </BaseModal>

    <BaseModal :is-visible="showUnsavedCreateConfirmModal" @close="cancelCloseCreateModal">
      <template v-slot:title>
        <h4 class="font-weight-bold">UNSAVED CHANGES</h4>
      </template>
      <template v-slot:body>
        <p class="text-center font-weight-bold h6">
          Are you sure you want to close?<br />
          <span class="text-danger">You have unsaved changes.</span>
        </p>
      </template>
      <template v-slot:footer>
        <div class="d-flex justify-content-center w-100">
          <button class="btn btn-sm btn-warning font-weight-bold" @click="confirmCloseCreateModal">
            CLOSE
          </button>
        </div>
      </template>
    </BaseModal>

    <BaseModal :is-visible="showUpdateModal" :close-button="false">
      <template v-slot:title><h4 class="font-weight-bold">UPDATE REVISION</h4></template>
      <template v-slot:body>
        <form @submit.prevent="submitUpdate">
          <div class="form-group">
            <label>
              Revision Number
              <span v-if="isUpdateFormInvalid" class="text-danger font-weight-bold h6">*</span>
            </label>

            <input
              @input="clearRevisionError"
              type="text"
              v-model="form.revision_number"
              class="form-control"
              :class="{ 'is-invalid': isUpdateFormInvalid || revisionErrors.revision_number }"
            />
            <small v-if="revisionErrors.revision_number" class="text-danger font-weight-bold">
              {{ revisionErrors.revision_number[0] }}
            </small>
            <small v-else-if="!form.revision_number.trim()" class="text-danger font-weight-bold">
              Revision number is required.
            </small>
            <small v-else-if="isUpdateFormatInvalid" class="text-danger font-weight-bold">
              Revision Number must be in format (e.g., 1.0 / 11.11 / 1.1.1)
            </small>
          </div>
        </form>
      </template>
      <template v-slot:footer>
        <button class="btn btn-sm btn-secondary" @click="onClickCancelUpdate">Cancel</button>
        <button
          class="btn btn-sm btn-primary"
          :disabled="isUpdateFormInvalid"
          @click="submitUpdate"
        >
          Update
        </button>
      </template>
    </BaseModal>

    <BaseModal :is-visible="showUnsavedConfirmModal" @close="cancelCloseUpdateModal">
      <template v-slot:title>
        <h4 class="font-weight-bold">UNSAVED CHANGES</h4>
      </template>
      <template v-slot:body>
        <p class="text-center font-weight-bold h6">
          Are you sure you want to close?<br />
          <span class="text-danger">You have unsaved changes.</span>
        </p>
      </template>
      <template v-slot:footer>
        <div class="d-flex justify-content-center w-100">
          <button class="btn btn-sm btn-warning font-weight-bold" @click="confirmCloseUpdateModal">
            CLOSE
          </button>
        </div>
      </template>
    </BaseModal>

    <BaseModal :is-visible="showDeleteModal" @close="showDeleteModal = false">
      <template v-slot:title>
        <h4 class="font-weight-bold">CONFIRM DELETION</h4>
      </template>
      <template v-slot:body>
        <p class="h6 text-center font-weight-bold mb-3">
          Are you sure you want to delete this revision number?<br />
          <span class="text-danger">This action cannot be undone.</span>
        </p>
        <div class="form-group">
          <label class="font-weight-bold">Enter your password to confirm:</label>
          <input
            @input="clearDeleteError"
            type="password"
            class="form-control form-control-sm"
            v-model="deletePassword"
            placeholder="Password"
          />
          <small v-if="deleteError" class="text-danger font-weight-bold d-block mt-2">
            {{ deleteError }}
          </small>
        </div>
      </template>
      <template v-slot:footer>
        <div class="d-flex justify-content-center w-100">
          <button
            class="btn btn-sm btn-danger font-weight-bold"
            @click="submitDelete"
            :disabled="!deletePassword.trim()"
          >
            DELETE
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import BaseModal from '@/components/common/BaseModal.vue';
import loading from '@/components/elements/AppLoading.vue';
import { AlertService } from '@/services/AlertService';
import { RevisionService } from '@/services/RevisionService';

export default {
  components: { BaseModal, loading },
  data() {
    return {
      deletePassword: '',
      deleteError: '',
      revisionErrors: {},
      pristineCreateForm: null,
      showUnsavedCreateConfirmModal: false,
      pristineForm: null,
      showUnsavedConfirmModal: false,
      revisions: [],
      form: {
        id: null,
        revision_number: '',
      },
      currentPage: 1,
      perPage: 5,
      searchQuery: '',
      loading: false,
      showCreateModal: false,
      showUpdateModal: false,
      showDeleteModal: false,
      deleteTargetId: null,
    };
  },
  computed: {
    hasUnsavedCreateChanges() {
      if (!this.pristineCreateForm) return false;
      return this.form.revision_number !== this.pristineCreateForm.revision_number;
    },
    hasUnsavedChanges() {
      if (!this.pristineForm) return false;
      return this.form.revision_number !== this.pristineForm.revision_number;
    },
    isUpdateFormatInvalid() {
      const pattern = /^\d{1,3}\.\d{1,3}(\.\d{1,3})?$/;
      return !pattern.test(this.form.revision_number.trim());
    },
    isUpdateFormInvalid() {
      return !this.form.revision_number.trim() || this.isUpdateFormatInvalid;
    },
    isFormatInvalid() {
      const pattern = /^\d{1,3}\.\d{1,3}(\.\d{1,3})?$/; // Matches 1.1 to 999.999
      return !pattern.test(this.form.revision_number.trim());
    },
    isCreateFormInvalid() {
      return !this.form.revision_number.trim() || this.isFormatInvalid;
    },
    visiblePages() {
      const delta = 2;
      const start = Math.max(1, this.currentPage - delta);
      const end = Math.min(this.totalPages, this.currentPage + delta);
      return Array.from({ length: end - start + 1 }, (_, i) => i + start);
    },
    filteredRevisions() {
      const search = this.searchQuery.trim().toLowerCase();
      if (!search) return this.revisions;

      return this.revisions.filter(rev => {
        const revisionNumber = rev.revision_number?.toLowerCase() || '';
        const createdAt = this.formatDate(rev.created_at)?.toLowerCase() || '';
        const createdBy = rev.created_by_user
          ? `${rev.created_by_user.first_name} ${rev.created_by_user.last_name}`.toLowerCase()
          : '';

        return (
          revisionNumber.includes(search) ||
          createdAt.includes(search) ||
          createdBy.includes(search)
        );
      });
    },
    totalPages() {
      return Math.ceil(this.filteredRevisions.length / this.perPage) || 1;
    },
    paginatedRevisions() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredRevisions.slice(start, start + this.perPage);
    },
  },
  methods: {
    clearDeleteError() {
      this.deleteError = '';
    },
    clearRevisionError() {
      this.$delete(this.revisionErrors, 'revision_number');
    },
    onClickCancelCreate() {
      this.closeCreateModal();
    },
    onClickCancelUpdate() {
      this.closeUpdateModal();
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },
    resetForm() {
      this.form = {
        id: null,
        revision_number: '',
      };
    },
    openCreateModal() {
      this.resetForm();
      this.revisionErrors = {};
      this.showCreateModal = true;
      this.pristineCreateForm = { ...this.form };
    },
    closeCreateModal(force = false) {
      if (this.hasUnsavedCreateChanges && !force) {
        this.showCreateModal = false;
        this.showUnsavedCreateConfirmModal = true;
        return;
      }
      this.resetForm();
      this.showCreateModal = false;
    },
    confirmCloseCreateModal() {
      this.showUnsavedCreateConfirmModal = false;
      this.closeCreateModal(true);
    },

    cancelCloseCreateModal() {
      this.showUnsavedCreateConfirmModal = false;
      this.showCreateModal = true;
    },
    async submitCreate() {
      if (this.isCreateFormInvalid) return;
      try {
        await RevisionService.create({ revision_number: this.form.revision_number });
        await this.loadRevisions();

        this.resetForm();
        this.showCreateModal = false;
        this.showUnsavedCreateConfirmModal = false;

        AlertService.successAlert('Revision created successfully', 'CREATE');
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.revisionErrors = error.response.data.errors || {};
        } else {
          AlertService.errorAlert('Failed to create revision', 'ERROR');
        }
      }
    },
    openUpdateModal(rev) {
      this.form = { id: rev.id, revision_number: rev.revision_number };
      this.pristineForm = { ...this.form };
      this.revisionErrors = {};
      this.showUpdateModal = true;
    },
    closeUpdateModal(force = false) {
      if (this.hasUnsavedChanges && !force) {
        this.showUpdateModal = false;
        this.showUnsavedConfirmModal = true;
        return;
      }
      this.resetForm();
      this.showUpdateModal = false;
    },
    confirmCloseUpdateModal() {
      this.showUnsavedConfirmModal = false;
      this.closeUpdateModal(true);
    },

    cancelCloseUpdateModal() {
      this.showUnsavedConfirmModal = false;
      this.showUpdateModal = true;
    },
    async submitUpdate() {
      if (this.isUpdateFormInvalid) return;

      try {
        await RevisionService.update(this.form.id, {
          revision_number: this.form.revision_number,
        });
        await this.loadRevisions();

        this.resetForm();
        this.showUpdateModal = false;
        this.showUnsavedConfirmModal = false;

        AlertService.successAlert('Revision updated successfully', 'UPDATE');
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.revisionErrors = error.response.data.errors || {};
        } else {
          AlertService.errorAlert('Failed to update revision', 'ERROR');
        }
      }
    }
    ,
    confirmDelete(id) {
      this.deleteTargetId = id;
      this.showDeleteModal = true;
    },
    async submitDelete() {
      this.deleteError = '';
      try {
        await RevisionService.deleteWithPassword(this.deleteTargetId, this.deletePassword);
        await this.loadRevisions();

        this.showDeleteModal = false;
        this.deletePassword = '';
        this.deleteError = '';

        AlertService.successAlert('Revision deleted successfully', 'DELETE');
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.deleteError = 'Incorrect password. Please try again.';
        } else {
          AlertService.errorAlert('Failed to delete revision', 'ERROR');
        }
      }
    },
    async loadRevisions() {
      this.loading = true;
      try {
        const response = await RevisionService.getAll();
        this.revisions = response.data;
      } catch (error) {
        console.error('Fetch error', error);
      } finally {
        this.loading = false;
      }
    },
  },
  async created() {
    await this.loadRevisions();
  }
};
</script>
<style scoped>
th, td {
  text-align: center;
  vertical-align: middle;
}
</style>
